import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import './reports.css';

const DoctorAccount = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [accountData, setAccountData] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDoctors();
  }, [token, i18n.language]);

  useEffect(() => {
    if (selectedDoctor && dateRange.startDate && dateRange.endDate) {
      fetchDoctorAccount();
    }
  }, [selectedDoctor, dateRange, token, i18n.language]);

  const fetchDoctors = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/doctores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        setDoctors(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchDoctorAccount = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/doctor_account_report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({
          doctor_id: selectedDoctor,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }),
      });

      const result = await response.json();
      if (result.status) {
        setAccountData(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch doctor account');
      }
    } catch (error) {
      setError('Error fetching doctor account');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  const calculateTotals = () => {
    const totals = accountData.reduce((acc, item) => {
      acc.totalAppointments += parseInt(item.appointments_count) || 0;
      acc.totalRevenue += parseFloat(item.total_revenue) || 0;
      acc.doctorShare += parseFloat(item.doctor_share) || 0;
      acc.clinicShare += parseFloat(item.clinic_share) || 0;
      acc.paidAmount += parseFloat(item.paid_amount) || 0;
      acc.remainingAmount += parseFloat(item.remaining_amount) || 0;
      return acc;
    }, {
      totalAppointments: 0,
      totalRevenue: 0,
      doctorShare: 0,
      clinicShare: 0,
      paidAmount: 0,
      remainingAmount: 0
    });

    return totals;
  };

  const selectedDoctorData = doctors.find(doc => doc.id == selectedDoctor);

  // if (loading) {
  //   return (
  //     <div className="container tables bg-white mt-5">
  //       <div className="text-center">
  //         <div className="spinner-border" role="status">
  //           <span className="visually-hidden">Loading...</span>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  const totals = calculateTotals();

  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('reports.doctorAccount.title')}</h3>
      </div>

      <div className="report-filters">
        <div className="row">
          <div className="col-md-3">
            <label className="form-label">{t('reports.selectDoctor')}</label>
            <select
              className="form-control"
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
            >
              <option value="">{t('reports.selectDoctor')}</option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name} ({doctor.revenue || 0}%)
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('reports.startDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('reports.endDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {selectedDoctor && selectedDoctorData && (
        <div className="doctor-info">
          <h4>{selectedDoctorData.name}</h4>
          <p>{t('reports.revenuePercentage')}: {selectedDoctorData.revenue || 0}%</p>
        </div>
      )}

      {selectedDoctor && accountData.length > 0 && (
        <div className="report-summary">
          <div className="row">
            <div className="col-md-2">
              <div className="summary-card">
                <h5>{t('reports.totalAppointments')}</h5>
                <span className="summary-number">{totals.totalAppointments}</span>
              </div>
            </div>
            <div className="col-md-2">
              <div className="summary-card">
                <h5>{t('reports.totalRevenue')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalRevenue)}</span>
              </div>
            </div>
            <div className="col-md-2">
              <div className="summary-card">
                <h5>{t('reports.doctorShare')}</h5>
                <span className="summary-number">{formatCurrency(totals.doctorShare)}</span>
              </div>
            </div>
            <div className="col-md-2">
              <div className="summary-card">
                <h5>{t('reports.clinicShare')}</h5>
                <span className="summary-number">{formatCurrency(totals.clinicShare)}</span>
              </div>
            </div>
            <div className="col-md-2">
              <div className="summary-card">
                <h5>{t('reports.paidAmount')}</h5>
                <span className="summary-number">{formatCurrency(totals.paidAmount)}</span>
              </div>
            </div>
            <div className="col-md-2">
              <div className="summary-card">
                <h5>{t('reports.remainingAmount')}</h5>
                <span className="summary-number">{formatCurrency(totals.remainingAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedDoctor && (
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{t('reports.date')}</th>
                <th>{t('reports.appointmentsCount')}</th>
                <th>{t('reports.totalRevenue')}</th>
                <th>{t('reports.doctorShare')}</th>
                <th>{t('reports.clinicShare')}</th>
                <th>{t('reports.paidAmount')}</th>
                <th>{t('reports.remainingAmount')}</th>
                <th>{t('reports.revenuePercentage')}</th>
              </tr>
            </thead>
            <tbody>
              {accountData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    {t('reports.noAccountData')}
                  </td>
                </tr>
              ) : (
                accountData.map((item) => (
                  <tr key={item.date}>
                    <td>{formatDate(item.date)}</td>
                    <td>{item.appointments_count}</td>
                    <td>{formatCurrency(item.total_revenue)}</td>
                    <td>{formatCurrency(item.doctor_share)}</td>
                    <td>{formatCurrency(item.clinic_share)}</td>
                    <td>{formatCurrency(item.paid_amount)}</td>
                    <td>{formatCurrency(item.remaining_amount)}</td>
                    <td>{selectedDoctorData?.revenue || 0}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedDoctor && accountData.length > 0 && (
        <div className="account-summary">
          <div className="row">
            <div className="col-md-6">
              <div className="summary-section">
                <h4>{t('reports.accountSummary')}</h4>
                <div className="summary-item">
                  <span>{t('reports.totalRevenue')}:</span>
                  <span>{formatCurrency(totals.totalRevenue)}</span>
                </div>
                <div className="summary-item">
                  <span>{t('reports.doctorShare')}:</span>
                  <span>{formatCurrency(totals.doctorShare)}</span>
                </div>
                <div className="summary-item">
                  <span>{t('reports.paidAmount')}:</span>
                  <span>{formatCurrency(totals.paidAmount)}</span>
                </div>
                <div className="summary-item total">
                  <span>{t('reports.remainingAmount')}:</span>
                  <span>{formatCurrency(totals.remainingAmount)}</span>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="summary-section">
                <h4>{t('reports.paymentInstructions')}</h4>
                <p>{t('reports.paymentInstructionsText')}</p>
                <div className="payment-details">
                  <p><strong>{t('reports.doctorName')}:</strong> {selectedDoctorData?.name}</p>
                  <p><strong>{t('reports.revenuePercentage')}:</strong> {selectedDoctorData?.revenue || 0}%</p>
                  <p><strong>{t('reports.amountToPay')}:</strong> {formatCurrency(totals.remainingAmount)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorAccount;
